import { useState, useEffect } from 'react';
import { Mail, Calendar, RefreshCw, Trash2, AlertTriangle, CheckCircle } from 'lucide-react';
import { userService } from '../services/userService';
import type { UserInvitation } from '../types/user';
import { useLanguage } from '../contexts/LanguageContext';
import { useModal } from '../contexts/ModalContext';
import { log } from '../lib/logger';

export function PendingInvitesManager() {
  const { t } = useLanguage();
  const { showConfirm, toast } = useModal();
  const [invitations, setInvitations] = useState<UserInvitation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadInvitations();
  }, []);

  const loadInvitations = async () => {
    setLoading(true);
    const data = await userService.getInvitations();

    const usersData = await userService.getAllUsers();
    const existingEmails = new Set(usersData.map(u => u.email.toLowerCase()));
    const pendingInvitations = data.filter(inv =>
      !inv.accepted && !existingEmails.has(inv.email.toLowerCase())
    );

    setInvitations(pendingInvitations);
    setLoading(false);
  };

  const handleDeleteInvitation = async (invitationId: string) => {
    const invitation = invitations.find(inv => inv.id === invitationId);
    const confirmed = await showConfirm({
      title: 'Delete Invitation?',
      message: `Are you sure you want to delete the invitation for ${invitation?.email || 'this user'}?`,
      confirmText: 'Delete Invitation',
      cancelText: 'Cancel',
      variant: 'danger',
    });

    if (!confirmed) return;

    try {
      await userService.deleteInvitation(invitationId);
      toast.success(t('admin.toast.invitationDeletedSuccess'));
      await loadInvitations();
    } catch (error) {
      log.error('Error deleting invitation:', error);
      toast.error(t('admin.toast.invitationDeleteFailed'));
    }
  };

  const isExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date();
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 p-12 text-center">
        <RefreshCw className="w-12 h-12 mx-auto mb-4 text-blue-600 animate-spin" />
        <p className="text-gray-500 text-lg">{t('admin.loadingInvitations')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {t('admin.pendingInvitationsTitle')}
            </h2>
            <p className="text-gray-600 mt-1">
              {t('admin.pendingInvitationsDesc')}
            </p>
          </div>
          <button
            onClick={loadInvitations}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
            {t('common.refresh')}
          </button>
        </div>

        {invitations.length === 0 ? (
          <div className="text-center py-12">
            <Mail className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 text-lg">{t('admin.noPendingInvitations')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {invitations.map((invitation) => {
              const expired = isExpired(invitation.expires_at);

              return (
                <div
                  key={invitation.id}
                  className={`p-6 border rounded-lg ${
                    expired ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <Mail className="w-5 h-5 text-gray-400" />
                        <h3 className="text-lg font-semibold text-gray-900">
                          {invitation.email}
                        </h3>
                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                          invitation.role === 'admin'
                            ? 'bg-blue-100 text-blue-800'
                            : invitation.role === 'influencer'
                            ? 'bg-purple-100 text-purple-800'
                            : invitation.role === 'content_hub'
                            ? 'bg-lime-100 text-lime-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {invitation.role === 'admin'
                            ? t('admin.roleAdmin')
                            : invitation.role === 'influencer'
                            ? t('admin.roleInfluencer')
                            : invitation.role === 'content_hub'
                            ? t('admin.roleContentHubOnly')
                            : t('admin.roleAgent')}
                        </span>
                        {expired && (
                          <span className="flex items-center gap-1 px-3 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                            <AlertTriangle className="w-3 h-3" />
                            {t('admin.expired')}
                          </span>
                        )}
                        {!expired && (
                          <span className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                            <CheckCircle className="w-3 h-3" />
                            {t('admin.active')}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-6 mb-4 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span>
                            {t('admin.expiresOn')}: {new Date(invitation.expires_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div>
                          {t('admin.createdOn')}: {new Date(invitation.created_at).toLocaleDateString()}
                        </div>
                      </div>

                      <p className="mt-4 text-sm text-gray-500">
                        {t('admin.resendHint')}
                      </p>
                    </div>

                    <div className="ml-4">
                      <button
                        onClick={() => handleDeleteInvitation(invitation.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title={t('admin.deleteInvite')}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
